import { useState } from 'react'
import { CheckCircle, AlertCircle, FileText } from 'lucide-react'
import { LLMTxtForm } from '../components/LLMTxtForm'
import { LLMTxtPreview } from '../components/LLMTxtPreview'
import { ModeSelector } from '../components/ModeSelector'
import { AutoModeForm } from '../components/AutoModeForm'
import { SEOHead } from '../components/SEOHead'
import { LLMTxtFormData, GenerationMode, AIProvider, AutoModeProgress } from '../types'
import { generatorApi, autoModeApi } from '../services/api'

export function GeneratorPage() {
  // Common state
  const [loading, setLoading] = useState(false)
  const [previewContent, setPreviewContent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Mode selection
  const [selectedMode, setSelectedMode] = useState<GenerationMode>('manual')

  // Auto mode state
  const [autoModeProgress, setAutoModeProgress] = useState<AutoModeProgress | undefined>()
  const [generatedContent, setGeneratedContent] = useState<string | undefined>()
  const [autoModeError, setAutoModeError] = useState<string | null>(null)


  const validateFormData = (formData: LLMTxtFormData): string | null => {
    if (!formData.website_name.trim()) {
      return "Please enter your website name in the 'Website Name' field. This is the main title of your website or business."
    }

    if (!formData.website_summary.trim()) {
      return "Please write a summary of your website in the 'Website Summary' field. Describe what your website is about in 1-2 sentences."
    }

    if (formData.content_sections.length === 0) {
      return "Please add at least one content section. Click 'Add Section' to create a section like 'Blog Posts' or 'Services'."
    }

    for (let i = 0; i < formData.content_sections.length; i++) {
      const section = formData.content_sections[i]
      if (!section.name.trim()) {
        return `Please give Section ${i + 1} a name. For example: 'Blog Posts', 'Products', or 'Services'.`
      }

      if (section.urls.length === 0 || !section.urls[0].title.trim() || !section.urls[0].url.trim()) {
        return `Please add at least one URL to Section ${i + 1} ('${section.name}'). Include both a title and a web address.`
      }

      for (let j = 0; j < section.urls.length; j++) {
        const url = section.urls[j]
        if (!url.title.trim()) {
          return `Please add a title for URL ${j + 1} in section '${section.name}'. For example: 'Getting Started Guide' or 'About Us'.`
        }
        if (!url.url.trim()) {
          return `Please add a web address (URL) for '${url.title}' in section '${section.name}'. It should start with https://`
        }
        try {
          new URL(url.url)
        } catch {
          return `The URL for '${url.title}' in section '${section.name}' is not valid. Please make sure it starts with https:// and is a complete web address.`
        }
      }
    }

    return null
  }

  const handleGenerate = async (formData: LLMTxtFormData) => {
    try {
      setLoading(true)
      setError(null)
      setSuccess(false)

      // Validate form data first
      const validationError = validateFormData(formData)
      if (validationError) {
        setError(validationError)
        setLoading(false)
        return
      }

      const response = await generatorApi.generate(formData)

      if (response.success && response.data) {

        setSuccess(true)

        // Auto-download the file
        const blob = new Blob([response.data.content], { type: 'text/plain' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = 'llm.txt'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      } else {
        setError(response.error || 'Something went wrong while creating your llm.txt file. Please try again, or contact support if the problem continues.')
      }
    } catch (err) {
      setError('There was a problem connecting to our servers. Please check your internet connection and try again.')
    } finally {
      setLoading(false)
    }
  }

  const handlePreview = async (formData: LLMTxtFormData) => {
    try {
      setLoading(true)
      setError(null)

      // Validate form data first
      const validationError = validateFormData(formData)
      if (validationError) {
        setError(validationError)
        setLoading(false)
        return
      }

      const response = await generatorApi.preview(formData)

      if (response.success && response.data) {
        setPreviewContent(response.data.preview)
      } else {
        setError(response.error || 'Something went wrong while creating the preview. Please try again.')
      }
    } catch (err) {
      setError('There was a problem connecting to our servers. Please check your internet connection and try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleClosePreview = () => {
    setPreviewContent(null)
  }

  const handleDownloadFromPreview = () => {
    if (previewContent) {
      setSuccess(true)
    }
    setPreviewContent(null)
  }

  // Auto mode handlers
  const handleAutoModeGenerate = async (data: {
    websiteUrl: string;
    crawlMode: 'quick' | 'indepth';
    sitemapUrl?: string;
    provider: AIProvider;
    model: string;
    apiKey: string;
  }) => {
    // Declare timeouts array for cleanup access
    const timeouts: NodeJS.Timeout[] = []

    try {
      setLoading(true)
      setError(null)
      setAutoModeError(null)
      setGeneratedContent(undefined)

      // Step 1: Validate API key
      setAutoModeProgress({
        step: 'validating',
        message: 'Validating your API key with the AI provider...',
        progress: 5
      })

      const validationResponse = await autoModeApi.validateApiKey(data.provider, data.apiKey)
      if (!validationResponse.success || !validationResponse.data?.valid) {
        throw new Error(`API key validation failed for ${data.provider}. Please check your API key and ensure it has the necessary permissions.`)
      }

      // Step 2: Crawl website
      setAutoModeProgress({
        step: 'crawling',
        message: data.crawlMode === 'quick'
          ? 'Analyzing navigation and key pages...'
          : 'Discovering sitemap and analyzing comprehensive site structure...',
        progress: 15
      })

      const crawlResponse = await autoModeApi.crawlWebsite(data.websiteUrl, data.crawlMode, data.sitemapUrl)
      if (!crawlResponse.success || !crawlResponse.data?.crawling_result) {
        const errorMsg = crawlResponse.error || 'Unknown crawling error'
        throw new Error(`Failed to crawl website: ${errorMsg}. Please ensure the URL is accessible and try again.`)
      }

      // Step 3: Generate content with AI
      const pagesAnalyzed = crawlResponse.data.crawling_result.total_pages_analyzed || 1
      setAutoModeProgress({
        step: 'generating',
        message: `AI is analyzing content from ${pagesAnalyzed} page${pagesAnalyzed > 1 ? 's' : ''} and generating llm.txt file... This may take 30-60 seconds.`,
        progress: 25
      })

      // Start detailed progress tracking for AI generation
      const progressUpdates = [
        { delay: 2000, message: 'AI is analyzing your website content...', progress: 30 },
        { delay: 8000, message: 'AI is structuring the llm.txt format...', progress: 45 },
        { delay: 15000, message: 'AI is generating comprehensive sections...', progress: 60 },
        { delay: 25000, message: 'AI is finalizing and validating content...', progress: 80 },
        { delay: 35000, message: 'Almost complete! AI is performing final checks...', progress: 90 }
      ]



      progressUpdates.forEach(({ delay, message, progress }) => {
        const timeout = setTimeout(() => {
          setAutoModeProgress({
            step: 'generating',
            message,
            progress
          })
        }, delay)
        timeouts.push(timeout)
      })

      const generateResponse = await autoModeApi.generateAuto(
        data.provider,
        data.model,
        data.apiKey,
        crawlResponse.data.crawling_result
      )

      // Clear all progress timeouts
      timeouts.forEach(timeout => clearTimeout(timeout))

      if (!generateResponse.success || !generateResponse.data?.content) {
        const errorMsg = generateResponse.error || 'Unknown AI generation error'
        throw new Error(`AI generation failed: ${errorMsg}. This could be due to API rate limits, model availability, or content complexity. Please try again in a few moments or switch to manual mode.`)
      }

      // Step 4: Complete
      setAutoModeProgress({
        step: 'complete',
        message: 'Generation complete! Review your content below.',
        progress: 100
      })

      setGeneratedContent(generateResponse.data.content)

    } catch (err: any) {
      console.error('Auto mode generation error:', err)

      // Clear any pending progress timeouts
      timeouts.forEach((timeout: NodeJS.Timeout) => clearTimeout(timeout))

      let errorMessage = 'An unexpected error occurred. Please try again.'
      let progressMessage = 'Generation failed'

      if (err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. The AI service may be busy. Please try again in a few moments.'
        progressMessage = 'Request timed out'
      } else if (err.message?.includes('API key')) {
        errorMessage = err.message
        progressMessage = 'API key validation failed'
      } else if (err.message?.includes('crawl')) {
        errorMessage = err.message
        progressMessage = 'Website crawling failed'
      } else if (err.message?.includes('AI generation')) {
        errorMessage = err.message
        progressMessage = 'AI generation failed'
      } else if (err.message) {
        errorMessage = err.message
      }

      setAutoModeProgress({
        step: 'error',
        message: progressMessage,
        progress: 0,
        error: errorMessage
      })
      setAutoModeError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handleContentEdit = (content: string) => {
    setGeneratedContent(content)
  }

  const handleFinalizeContent = () => {
    if (generatedContent) {
      // Auto-download the file
      const blob = new Blob([generatedContent], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'llm.txt'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      setSuccess(true)
      setGeneratedContent(undefined)
      setAutoModeProgress(undefined)
    }
  }

  const handleModeChange = (mode: GenerationMode) => {
    setSelectedMode(mode)
    setError(null)
    setAutoModeError(null)
    setSuccess(false)
    setGeneratedContent(undefined)
    setAutoModeProgress(undefined)
    setPreviewContent(null)
  }

  return (
    <>
      <SEOHead
        title="Free llm.txt Generator - Create AI-Optimized Files in Minutes"
        description="Generate properly formatted llm.txt files for your website with our free tool. Help AI tools like ChatGPT, Claude, and Gemini better understand your content."
        keywords="llm.txt generator, AI SEO tool, ChatGPT optimization, Claude optimization, Gemini optimization, AI website optimization"
        canonical="https://llmtext.net/generator"
      />
      <div className="min-h-screen bg-gray-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="page-title">llm.txt Generator</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-6">
            Create a properly formatted llm.txt file for your website in just a few minutes.
          </p>
          {selectedMode === 'manual' && (
            <div className="flex flex-col items-center">
              <button
                onClick={() => {
                  try {
                    (window as any).setLLMTxtDemoData?.()
                    setError(null)
                    setSuccess(false)
                  } catch {}
                }}
                className="btn-secondary btn-wide flex items-center mb-3"
              >
                <FileText className="h-4 w-4 mr-2" />
                Try Sample Data
              </button>
              <p className="text-sm text-gray-500 max-w-md text-center">
                Not sure how to fill out the form? Click the demo button to see an example using our own website data.
              </p>
            </div>
          )}
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-8 max-w-4xl mx-auto">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-green-800">
                    llm.txt Generated Successfully!
                  </h3>
                  <p className="text-sm text-green-700 mt-1">
                    Your llm.txt file has been downloaded. Upload it to your website's root directory.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {(error || autoModeError) && (
          <div className="mb-8 max-w-4xl mx-auto">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3" />
                <div>
                  <h3 className="text-sm font-medium text-red-800">
                    Error
                  </h3>
                  <p className="text-sm text-red-700 mt-1">
                    {error || autoModeError}
                  </p>
                  {selectedMode === 'auto' && autoModeError && (
                    <p className="text-sm text-red-600 mt-2">
                      <button
                        onClick={() => handleModeChange('manual')}
                        className="underline hover:no-underline"
                      >
                        Switch to Manual Mode
                      </button>
                      {' '}to create your llm.txt file step-by-step.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mode Selection */}
        <div className="max-w-4xl mx-auto mb-8">
          <ModeSelector
            selectedMode={selectedMode}
            onModeChange={handleModeChange}
          />
        </div>

        {/* Form */}
        <div className="max-w-4xl mx-auto">
          {selectedMode === 'manual' ? (
            <LLMTxtForm
              onGenerate={handleGenerate}
              onPreview={handlePreview}
              loading={loading}
            />
          ) : (
            <AutoModeForm
              onGenerate={handleAutoModeGenerate}
              loading={loading}
              progress={autoModeProgress}
              generatedContent={generatedContent}
              onContentEdit={handleContentEdit}
              onFinalizeContent={handleFinalizeContent}
            />
          )}
        </div>

        {/* Implementation Instructions */}
        <div className="max-w-4xl mx-auto mt-12">
          <div className="card">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">
              Next Steps: How to Implement
            </h2>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  1
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Upload to Your Website
                  </h3>
                  <p className="text-gray-600">
                    Save the downloaded file as <code className="bg-gray-100 px-2 py-1 rounded">llm.txt</code> and upload it to your website's root directory (the same level as your homepage).
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  2
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Verify Accessibility
                  </h3>
                  <p className="text-gray-600">
                    Make sure your file is accessible at <code className="bg-gray-100 px-2 py-1 rounded">https://yourwebsite.com/llm.txt</code>. You can use our verification tool to check this.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                  3
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Keep It Updated
                  </h3>
                  <p className="text-gray-600">
                    Update your llm.txt file whenever you add new important content to your website to ensure AI tools always have the latest information.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="/verification"
                  className="btn-outline"
                >
                  Verify Your llm.txt
                </a>
                <a
                  href="/implementation-guide"
                  className="btn-secondary"
                >
                  Detailed Implementation Guide
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Preview Modal */}
        {previewContent && (
          <LLMTxtPreview
            content={previewContent}
            onClose={handleClosePreview}
            onDownload={handleDownloadFromPreview}
          />
        )}
      </div>
    </div>
    </>
  )
}
